import { useState } from 'react';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProcedureCodeSearch, type DentalProcedureCodeRow } from '@/hooks/useProcedureCodes';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';

interface ProcedureCodePickerProps {
  onSelect: (row: DentalProcedureCodeRow) => void;
  onClose?: () => void;
  selectedCode?: string | null;
  /** If true, wraps in a modal overlay */
  asModal?: boolean;
}

export function ProcedureCodePicker({
  onSelect,
  onClose,
  selectedCode,
  asModal = false,
}: ProcedureCodePickerProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { data: results, isLoading } = useProcedureCodeSearch(query);

  const inner = (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="mb-2 flex-shrink-0">
        <Input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('dental.shortcuts.pickerSearch', 'Søg ydelseskode eller navn…')}
          leadingIcon={<Search className="h-4 w-4" aria-hidden />}
        />
      </div>

      {/* Results list */}
      <div className="flex-1 overflow-y-auto min-h-0 space-y-0.5">
        {isLoading ? (
          <div className="py-6 text-center text-sm text-ds-muted">{t('app.loading')}</div>
        ) : results.length === 0 ? (
          <div className="py-6 text-center text-sm text-ds-muted">
            {t('dental.shortcuts.pickerNoResults', 'Ingen resultater')}
          </div>
        ) : (
          results.map((row) => (
            <button
              key={row.code}
              type="button"
              onClick={() => onSelect(row)}
              className={cn(
                'w-full rounded-lg px-3 py-2 text-left transition-colors',
                'hover:bg-ds-teal-tint',
                selectedCode === row.code && 'bg-ds-teal-tint',
              )}
            >
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-mono font-semibold text-ds-teal shrink-0">
                  {row.code}
                </span>
                <span className="text-sm font-medium text-ds-text line-clamp-1">
                  {row.name_da}
                </span>
              </div>
              <div className="mt-0.5 flex items-center gap-2">
                <span className="text-xs text-ds-muted">{row.category}</span>
                {row.default_price_dkk != null && (
                  <span className="text-xs text-ds-muted">
                    {Number(row.default_price_dkk).toLocaleString('da-DK', {
                      style: 'currency',
                      currency: 'DKK',
                      maximumFractionDigits: 0,
                    })}
                  </span>
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );

  if (!asModal) return inner;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-ds-scrim p-4">
      <div className="mx-auto mt-8 flex max-w-md flex-col rounded-xl bg-ds-card shadow-pop" style={{ maxHeight: '80vh' }}>
        <div className="flex flex-shrink-0 items-center justify-between border-b border-ds-line p-4">
          <h3 className="text-base font-medium text-ds-text">
            {t('dental.shortcuts.pickerTitle', 'Vælg ydelseskode')}
          </h3>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-ds-muted hover:text-ds-text"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-hidden p-4">{inner}</div>
      </div>
    </div>
  );
}
